import { useEffect, useSyncExternalStore } from 'react';
import { isAmr } from '../course';
import {
  emptyProgress, mergeProgress, notesHistory, parseProgress, sameProgress,
  STUDY_KEY_PATTERN, validateProgress, validId,
  type ChapterProgress, type ProgressField, type StudyProgress,
} from './progress-model';
export type { AnswerProgress, ChapterProgress, StudyProgress } from './progress-model';
export { emptyProgress, mergeProgress, parseProgress, validateProgress } from './progress-model';
export { MAX_NOTES_LENGTH, MAX_PROGRESS_BYTES } from './progress-model';

export type SyncStatus = 'local' | 'saving' | 'synced' | 'offline' | 'unconfigured';
export interface ProgressSnapshot { progress: StudyProgress; studyKey: string; status: SyncStatus; error: string | null; localSaved: boolean }
interface StorageLike { getItem(key: string): string | null; setItem(key: string, value: string): void }
interface StoreOptions {
  storage: StorageLike;
  fetch: (url: string, init?: RequestInit) => Promise<Response>;
  randomUUID: () => string;
  initialStudyKey?: string;
  now?: () => number;
  debounceMs?: number;
}
const KEY_STORAGE = 'learncfd.study-key';
const DATA_PREFIX = 'learncfd.progress.';
/** Separate default study identities; namespaced chapter IDs also protect merged backups. */
export function courseStorage(storage: StorageLike, amr: boolean): StorageLike {
  const key = (value: string) => amr && value === KEY_STORAGE ? 'learncfd.polycfdamr.study-key' : value;
  return { getItem: value => storage.getItem(key(value)), setItem: (value, data) => storage.setItem(key(value), data) };
}
class SyncError extends Error { constructor(message: string, public status: SyncStatus) { super(message); } }

export function studyKeyFromText(text: string): string {
  let key = text.trim();
  if (key.includes('#')) key = new URLSearchParams(key.slice(key.indexOf('#') + 1)).get('study') ?? '';
  if (!STUDY_KEY_PATTERN.test(key)) throw new Error('Use a complete LearnCFD study key or private study link.');
  return key.toLowerCase();
}

/** A single local-first store: sync always reads, merges, and writes with revision protection. */
export class ProgressStore {
  private snapshot: ProgressSnapshot;
  private listeners = new Set<() => void>();
  private options: StoreOptions;
  private actor: string;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private flight: Promise<void> | null = null;
  private generation = 0;
  private rerun = false;
  private profiles = new Map<string, StudyProgress>();

  constructor(options: StoreOptions) {
    this.options = options;
    this.actor = options.randomUUID();
    let error: string | null = null;
    let studyKey = options.randomUUID();
    let progress = emptyProgress();
    try {
      const savedKey = options.initialStudyKey ?? options.storage.getItem(KEY_STORAGE);
      if (savedKey && STUDY_KEY_PATTERN.test(savedKey)) studyKey = savedKey.toLowerCase();
      const raw = options.storage.getItem(DATA_PREFIX + studyKey);
      if (raw) progress = mergeProgress(parseProgress(raw), emptyProgress());
      options.storage.setItem(KEY_STORAGE, studyKey);
    } catch { error = 'Browser storage could not be read. Export a backup before leaving this page.'; }
    this.snapshot = { progress, studyKey, status: 'local', error, localSaved: !error };
    this.profiles.set(studyKey, progress);
  }

  getSnapshot = (): ProgressSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private publish(patch: Partial<ProgressSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }
  private time(): number { return Math.max((this.options.now ?? Date.now)(), this.snapshot.progress.updatedAt + 1); }
  private persist(progress: StudyProgress): string | null {
    this.profiles.set(this.snapshot.studyKey, progress);
    try { this.options.storage.setItem(DATA_PREFIX + this.snapshot.studyKey, JSON.stringify(progress)); return null; }
    catch { return 'Browser storage is full or unavailable. Export a backup; cloud sync will still be attempted.'; }
  }
  private schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { void this.sync(); }, this.options.debounceMs ?? 700);
  }
  private commit(progress: StudyProgress) {
    const checked = validateProgress(progress);
    const error = this.persist(checked);
    this.publish({ progress: checked, status: 'saving', error, localSaved: !error });
    this.schedule();
  }

  updateChapter = (id: string, partial: Partial<ChapterProgress>): void => {
    if (!validId(id)) throw new Error('Invalid chapter identifier.');
    const at = this.time();
    const current = this.snapshot.progress.chapters[id] ?? { updatedAt: 0 };
    const chapter: ChapterProgress = { ...current, updatedAt: at, fieldTimes: { ...current.fieldTimes } };
    for (const field of ['read', 'bookmarked', 'notes', 'confidence', 'challengeDone'] as ProgressField[]) {
      if (partial[field] !== undefined) {
        Object.assign(chapter, { [field]: partial[field] });
        chapter.fieldTimes![field] = at;
      }
    }
    if (partial.notes !== undefined) {
      chapter.noteActor = this.actor;
      // Snapshot editing sessions, not every keystroke; remote merges retain competing versions.
      const oldAt = current.fieldTimes?.notes ?? current.updatedAt;
      const stableHistory = (current.noteHistory ?? []).filter(note => note.text !== current.notes || at - note.updatedAt >= 30_000);
      const previous = { ...current, notes: at - oldAt >= 30_000 ? current.notes : undefined, noteHistory: stableHistory };
      chapter.noteHistory = notesHistory(previous, { ...chapter, noteHistory: [] }).filter(note => note.text !== chapter.notes);
    }
    try { this.commit({ ...this.snapshot.progress, updatedAt: at, chapters: { ...this.snapshot.progress.chapters, [id]: chapter } }); }
    catch (error) { this.publish({ error: error instanceof Error ? error.message : 'Progress could not be saved.' }); }
  };

  recordAnswer = (chapterId: string, questionId: string, correct: boolean): void => {
    if (!validId(chapterId) || !validId(questionId)) throw new Error('Invalid exercise identifier.');
    const at = this.time();
    const chapter = this.snapshot.progress.chapters[chapterId] ?? { updatedAt: 0 };
    const oldAnswer = chapter.answers?.[questionId];
    const counters = { ...(oldAnswer?.counters ?? (oldAnswer ? { legacy: oldAnswer.attempts } : {})) };
    counters[this.actor] = (counters[this.actor] ?? 0) + 1;
    const answer = { correct, attempts: Object.values(counters).reduce((sum, count) => sum + count, 0), updatedAt: at, counters };
    try {
      this.commit({ ...this.snapshot.progress, updatedAt: at, chapters: {
        ...this.snapshot.progress.chapters,
        [chapterId]: { ...chapter, updatedAt: at, answers: { ...chapter.answers, [questionId]: answer } },
      } });
    } catch (error) { this.publish({ error: error instanceof Error ? error.message : 'Answer could not be saved.' }); }
  };

  setLastChapter = (id: string): void => {
    if (!validId(id) || this.snapshot.progress.lastChapter === id) return;
    const at = this.time();
    this.commit({ ...this.snapshot.progress, lastChapter: id, lastChapterAt: at, updatedAt: at });
  };

  // Compact exports are always re-importable within the same bounded payload size.
  exportProgress = (): string => JSON.stringify(this.snapshot.progress);
  importProgress = (text: string): void => {
    const incoming = parseProgress(text);
    this.commit(mergeProgress(this.snapshot.progress, incoming));
  };

  private async request(key: string, method = 'GET', body?: unknown): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12_000);
    try {
      const response = await this.options.fetch('/api/progress', {
        method, headers: { 'X-Study-Key': key, ...(body ? { 'Content-Type': 'application/json' } : {}) },
        cache: 'no-store', credentials: 'same-origin', signal: controller.signal,
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      if (!response.headers.get('Content-Type')?.includes('application/json')) {
        if (response.ok || response.status === 404) throw new SyncError('This preview saves in this browser. Cloud sync becomes available on Cloudflare Pages.', 'local');
        throw new SyncError('Cloud saving is unavailable. Your progress is saved in this browser.', 'offline');
      }
      if (!response.ok && response.status !== 409) {
        const data = await response.json() as { error?: string; code?: string };
        throw new SyncError(data.error ?? 'Cloud progress could not be saved.', data.code === 'unconfigured' ? 'unconfigured' : 'offline');
      }
      return response;
    } finally { clearTimeout(timeout); }
  }
  private async getRemote(key: string): Promise<{ progress: StudyProgress; revision: number }> {
    const response = await this.request(key);
    const data = await response.json() as { progress: unknown; revision: number };
    if (!Number.isSafeInteger(data.revision) || data.revision < 0) throw new Error('Cloud returned an invalid revision.');
    return { progress: validateProgress(data.progress), revision: data.revision };
  }

  sync = (): Promise<void> => {
    clearTimeout(this.timer);
    if (this.flight) { this.rerun = true; return this.flight; }
    const generation = this.generation;
    const key = this.snapshot.studyKey;
    this.publish({ status: 'saving' });
    this.flight = (async () => {
      try {
        for (let attempt = 0; attempt < 5; attempt++) {
          const remote = await this.getRemote(key);
          if (generation !== this.generation) return;
          const merged = validateProgress(mergeProgress(this.snapshot.progress, remote.progress));
          const storageError = this.persist(merged);
          this.publish({ progress: merged, error: storageError, localSaved: !storageError });
          if (sameProgress(merged, remote.progress)) { this.publish({ status: 'synced' }); return; }
          const response = await this.request(key, 'PUT', { progress: merged, revision: remote.revision });
          if (generation !== this.generation) return;
          if (response.status === 409) continue;
          // Edits made while the PUT was in flight must be sent before claiming synchronization.
          if (!sameProgress(this.snapshot.progress, merged)) continue;
          this.publish({ status: 'synced', error: storageError });
          return;
        }
        throw new SyncError('Another device is actively saving. Local progress is safe; try Sync again.', 'offline');
      } catch (error) {
        if (generation !== this.generation) return;
        this.publish({ status: error instanceof SyncError ? error.status : 'offline', error: error instanceof SyncError ? error.message : 'Cloud connection interrupted. Your progress remains in this browser; sync will retry.' });
      }
    })().finally(() => {
      this.flight = null;
      if (this.rerun) { this.rerun = false; this.schedule(); }
    });
    return this.flight;
  };

  connectStudyKey = async (text: string): Promise<void> => {
    const key = studyKeyFromText(text);
    // Resolve first. A mistyped/unavailable link never discards the current study profile.
    const remote = await this.getRemote(key);
    let local = this.profiles.get(key) ?? emptyProgress();
    let storageError: string | null = null;
    try {
      const raw = this.options.storage.getItem(DATA_PREFIX + key);
      if (raw) local = mergeProgress(local, parseProgress(raw));
      this.options.storage.setItem(KEY_STORAGE, key);
    } catch { storageError = 'This browser cannot retain your study profile. Keep your private study link and export a backup before leaving.'; }
    this.generation++;
    clearTimeout(this.timer);
    this.publish({ studyKey: key, progress: mergeProgress(local, remote.progress), status: 'saving', error: storageError, localSaved: !storageError });
    const progressError = this.persist(this.snapshot.progress);
    if (progressError) this.publish({ localSaved: false, error: progressError });
    if (this.flight) await this.flight;
    await this.sync();
  };

  receiveStorage = (key: string | null, value: string | null): void => {
    if (key !== DATA_PREFIX + this.snapshot.studyKey || !value) return;
    try {
      const incoming = parseProgress(value);
      const merged = mergeProgress(this.snapshot.progress, incoming);
      if (!sameProgress(merged, this.snapshot.progress)) {
        const error = this.persist(merged);
        this.publish({ progress: merged, status: 'saving', error, localSaved: !error });
        this.schedule();
      }
    } catch { /* An unrelated tab cannot invalidate this tab's checked copy. */ }
  };

  dispose = (): void => { clearTimeout(this.timer); this.generation++; this.listeners.clear(); };
}

let singleton: ProgressStore | undefined;
export function getProgressStore(): ProgressStore {
  if (!singleton) {
    let initialStudyKey: string | undefined;
    if (window.location.hash.startsWith('#study=')) {
      try { initialStudyKey = studyKeyFromText(window.location.hash); } catch { /* Keep the existing profile on malformed links. */ }
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/`);
    }
    // Access to localStorage itself can throw when browser storage is disabled.
    let storage: StorageLike;
    try { storage = window.localStorage; }
    catch { storage = { getItem: () => null, setItem: () => { throw new Error('Storage unavailable'); } }; }
    singleton = new ProgressStore({ storage: courseStorage(storage, isAmr), fetch: (url, init) => window.fetch(url, init), randomUUID: () => crypto.randomUUID(), initialStudyKey });
  }
  return singleton;
}

export function useProgress() {
  const store = getProgressStore();
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  useEffect(() => {
    void store.sync();
    const sync = () => { if (document.visibilityState === 'visible') void store.sync(); };
    const storage = (event: StorageEvent) => store.receiveStorage(event.key, event.newValue);
    window.addEventListener('online', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('storage', storage);
    const interval = setInterval(sync, 45_000);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('storage', storage);
      clearInterval(interval);
    };
  }, [store]);
  return {
    ...snapshot, updateChapter: store.updateChapter, recordAnswer: store.recordAnswer,
    setLastChapter: store.setLastChapter, sync: store.sync, importProgress: store.importProgress,
    exportProgress: store.exportProgress, connectStudyKey: store.connectStudyKey,
  };
}
